/**
 * ScheduleManager.jsx — Mesai Planı Yönetimi (CRUD)
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Save, X, Trash2, Edit3, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { workScheduleApi } from '../../../api/axios';

const DEFAULT_FORM = {
  name: '',
  schedule_type: 'FIXED',
  start_time: '08:30',
  end_time: '17:30',
  break_minutes: 60,
  grace_in_minutes: 10,
  grace_out_minutes: 5,
  late_after_minutes: 0,
  early_leave_after_minutes: 0,
  overtime_after_minutes: 30,
  core_start_time: '',
  core_end_time: '',
  crosses_midnight: false,
  is_active: true,
};

const ScheduleManager = ({ templates = [], loading, onRefresh }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (tmpl) => {
    setEditingId(tmpl.id);
    setForm({
      name: tmpl.name || '',
      schedule_type: tmpl.schedule_type || 'FIXED',
      start_time: tmpl.start_time || '08:30',
      end_time: tmpl.end_time || '17:30',
      break_minutes: tmpl.break_minutes ?? 60,
      grace_in_minutes: tmpl.grace_in_minutes ?? 10,
      grace_out_minutes: tmpl.grace_out_minutes ?? 5,
      late_after_minutes: tmpl.late_after_minutes ?? 0,
      early_leave_after_minutes: tmpl.early_leave_after_minutes ?? 0,
      overtime_after_minutes: tmpl.overtime_after_minutes ?? 30,
      core_start_time: tmpl.core_start_time || '',
      core_end_time: tmpl.core_end_time || '',
      crosses_midnight: tmpl.crosses_midnight || false,
      is_active: tmpl.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await workScheduleApi.updateTemplate(editingId, form);
        toast.success(t('schedule_updated', 'Mesai planı güncellendi.'));
      } else {
        await workScheduleApi.createTemplate(form);
        toast.success(t('schedule_created', 'Mesai planı oluşturuldu.'));
      }
      setShowModal(false);
      onRefresh?.();
    } catch {
      toast.error(t('err_schedule_save', 'Mesai planı kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(t('schedule_delete_confirm', `"${name}" mesai planını silmek istediğinize emin misiniz?`))) return;
    try {
      await workScheduleApi.deleteTemplate(id);
      toast.success(t('schedule_deleted', 'Mesai planı silindi.'));
      onRefresh?.();
    } catch {
      toast.error(t('err_schedule_delete', 'Mesai planı silinemedi.'));
    }
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">
            {t('schedule_kicker', 'Mesai Planı Yönetimi')}
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-900">
            {t('schedule_title', 'Mesai Planı Şablonları')}
          </h3>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 transition-colors"
        >
          <Plus size={14} />
          {t('schedule_add', 'Yeni Şablon')}
        </button>
      </div>

      {/* Şablon listesi */}
      {templates.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">{t('schedule_empty', 'Henüz mesai planı şablonu yok.')}</p>
      ) : (
        <div className="space-y-2">
          {templates.map((tmpl) => (
            <div
              key={tmpl.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 transition-colors ${
                tmpl.is_active ? 'border-slate-100 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'
              }`}
            >
              <div>
                <div className="font-bold text-slate-900 text-sm">{tmpl.name}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-[10px] font-bold text-slate-500">
                  <span>{tmpl.schedule_type === 'FIXED' ? '🕐 Sabit' : tmpl.schedule_type === 'FLEXIBLE' ? '🔄 Esnek' : tmpl.schedule_type}</span>
                  <span>⏰ {tmpl.start_time} - {tmpl.end_time}</span>
                  <span>☕ {tmpl.break_minutes}dk mola</span>
                  {!tmpl.is_active && <span className="text-rose-500">❌ Pasif</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => openEdit(tmpl)}
                  className="rounded-lg p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors"
                  title={t('edit', 'Düzenle')}
                >
                  <Edit3 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(tmpl.id, tmpl.name)}
                  className="rounded-lg p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  title={t('delete', 'Sil')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">
                {editingId ? t('schedule_edit', 'Mesai Planını Düzenle') : t('schedule_new', 'Yeni Mesai Planı')}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_name', 'Plan Adı')}</span>
                <input required value={form.name} onChange={(e) => updateField('name', e.target.value)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_type', 'Tip')}</span>
                  <select value={form.schedule_type} onChange={(e) => updateField('schedule_type', e.target.value)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                    <option value="FIXED">Sabit</option>
                    <option value="FLEXIBLE">Esnek</option>
                    <option value="SHIFT">Vardiya</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_break', 'Mola (dk)')}</span>
                  <input type="number" value={form.break_minutes} onChange={(e) => updateField('break_minutes', parseInt(e.target.value) || 0)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_start', 'Başlangıç')}</span>
                  <input type="time" value={form.start_time} onChange={(e) => updateField('start_time', e.target.value)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_end', 'Bitiş')}</span>
                  <input type="time" value={form.end_time} onChange={(e) => updateField('end_time', e.target.value)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_grace_in', 'Giriş Tolerans (dk)')}</span>
                  <input type="number" value={form.grace_in_minutes} onChange={(e) => updateField('grace_in_minutes', parseInt(e.target.value) || 0)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{t('schedule_overtime', 'Fazla Mesai Sonrası (dk)')}</span>
                  <input type="number" value={form.overtime_after_minutes} onChange={(e) => updateField('overtime_after_minutes', parseInt(e.target.value) || 0)} className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                </label>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active} onChange={(e) => updateField('is_active', e.target.checked)} className="rounded border-slate-300 text-cyan-600" />
                <span className="text-xs font-bold text-slate-600">{t('schedule_active', 'Aktif')}</span>
              </label>
              <div className="flex items-center gap-2 pt-2">
                <button type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-xl bg-cyan-600 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-700 disabled:opacity-50">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {t('save', 'Kaydet')}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="rounded-xl px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100">
                  {t('cancel', 'İptal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;